/**
 * Decides which body slot to render: the in-place editor, the framed
 * automation card, or the plain message body. Extracted from
 * `Message.tsx` so that file stays under the 400-LOC ceiling — the
 * three-way conditional was the bulkiest piece of the render.
 *
 * The card path applies only to automation-fired rows (`metadata.agent
 * === true` and not a coworker DM). Coworker DMs keep the unframed
 * look so they read like normal chat.
 */
import type { TiptapEditorRef } from "../TiptapEditor";
import type { RefObject } from "react";
import type { MessageProps } from "./message-props";
import { AutomationMessageCard } from "../AutomationMessageCard";
import { MessageBody } from "./MessageBody";
import { MessageEditor } from "./MessageEditor";

interface MessageBodyAreaProps {
  isEditing: boolean;
  editEditorRef: RefObject<TiptapEditorRef | null>;
  onSave: () => void;
  onCancel: () => void;
  isAgentAuthored: boolean;
  isCoworkerAuthored: boolean;
  automationApps: ReadonlyArray<{
    slug: string;
    name?: string;
    img_src?: string;
  }>;
  message: MessageProps["message"];
  quotedSource: MessageProps["quotedSource"];
  quotedSourceAuthorName: MessageProps["quotedSourceAuthorName"];
  quotedSourceAuthorAvatarURL?: string | null;
  isOwn: boolean;
  mentionableUsers: { id: string; display_name: string }[];
  onUserClick?: (userId: string) => void;
  channels?: { id: string; name: string }[];
  onChannelClick?: (channelId: string) => void;
  attachmentList: NonNullable<MessageProps["attachments"]>;
  linkPreviewList: NonNullable<MessageProps["linkPreviews"]>;
  onThreadClick?: (messageId: string) => void;
  /** Inline-reply citation pill click — scrolls the channel timeline
   *  to the source row in-place (Discord-shape). */
  onScrollToMessage?: (messageId: string) => void;
}

export function MessageBodyArea(props: MessageBodyAreaProps) {
  if (props.isEditing) {
    return (
      <MessageEditor
        editorRef={props.editEditorRef}
        onSave={props.onSave}
        onCancel={props.onCancel}
      />
    );
  }
  const body = (
    <MessageBody
      message={props.message}
      quotedSource={props.quotedSource}
      quotedSourceAuthorName={props.quotedSourceAuthorName}
      quotedSourceAuthorAvatarURL={props.quotedSourceAuthorAvatarURL}
      isOwn={props.isOwn}
      mentionableUsers={props.mentionableUsers}
      onUserClick={props.onUserClick}
      channels={props.channels}
      onChannelClick={props.onChannelClick}
      attachmentList={props.attachmentList}
      linkPreviewList={props.linkPreviewList}
      onThreadClick={props.onThreadClick}
      onScrollToMessage={props.onScrollToMessage}
    />
  );
  // AutomationMessageCard renders only when the automation engine has
  // actually denormalized `apps_used` onto the message — that's the
  // load-bearing signal that the engine wrote it. The previous predicate
  // (`isAgentAuthored && !isCoworkerAuthored`) over-fired: human users
  // posting via the `ano` CLI and external agents posting via MCP both
  // set `metadata.agent = true` (see `server/mcp/mcp-http/text-send-routes.ts`
  // `sendMetadata()`), so anything passing through MCP-HTTP was getting
  // wrapped in the automation card. Now CLI messages render as regular
  // member messages (with a "VIA CLI" pill in the header) and external
  // agents render as regular member messages (with the Cloud icon).
  if ((props.automationApps?.length ?? 0) > 0) {
    return (
      <AutomationMessageCard
        content={props.message.content}
        apps={props.automationApps}
      >
        {body}
      </AutomationMessageCard>
    );
  }
  return body;
}
